
import { storage } from '../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { slugify } from './slugify';
import { convertToWebP } from './imageOptimizer';

const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

interface UploadOptions {
  optimizeToWebP?: boolean;
}

export const uploadFile = async (file: File, path: string, baseName?: string, options?: UploadOptions): Promise<string> => {
  const fileToUpload = options?.optimizeToWebP ? await convertToWebP(file) : file;

  const uniqueId = generateUniqueId();
  const fileExtension = fileToUpload.name.split('.').pop()?.toLowerCase() || 'jpg';
  const originalNameWithoutExt = fileToUpload.name.replace(new RegExp(`\\.${fileExtension}$`, 'i'), '');

  const slugBaseName = baseName ? slugify(baseName) : slugify(originalNameWithoutExt);

  const uniqueFileName = `${slugBaseName}-${uniqueId}.${fileExtension}`;
  const storageRef = ref(storage, `${path}/${uniqueFileName}`);
  
  // Use uploadBytesResumable instead of uploadBytes for large file support (chunked uploads)
  const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

  return new Promise((resolve, reject) => {
    uploadTask.on('state_changed',
      (snapshot) => {
        // Optional: We can track progress here in the future
        // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        // console.log('Upload is ' + progress + '% done');
      },
      (error) => {
        // Handle unsuccessful uploads
        reject(error);
      },
      async () => {
        // Handle successful uploads on complete
        try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
        } catch (error) {
            reject(error);
        }
      }
    );
  });
};

export const deleteFileFromUrl = async (fileUrl: string): Promise<void> => {
    const isFirebaseUrl = /firebasestorage\.(googleapis\.com|app)/.test(fileUrl);
    if (!isFirebaseUrl) {
        console.warn('Attempted to delete a non-firebase URL:', fileUrl);
        return;
    }
    try {
        const storageRef = ref(storage, fileUrl);
        await deleteObject(storageRef);
    } catch (error: any) {
        if (error.code === 'storage/object-not-found') {
            console.warn('File to delete not found in storage:', fileUrl);
        } else {
            console.error('Error deleting file from storage:', error);
            throw error;
        }
    }
};
